import { EventEmitter } from 'events'
import util from 'util'
import _ from 'lodash'
import Dnd from './dnd'

/**
 * Porter is a general DND object manager that allows DOMNodes to be dragged and
 * dropped to any specified location.  DOMNodes that are allowed to be dragged
 * are called packages and are identified by "porter-package" css class.  DOMNodes
 * that accept packages are called destinations and are identified by the
 * "porter-destination" css class.  Packages can simulataneously be destinatons,
 * they are not mutually exclusive.
 *
 * Porter emits the following events
 * @fires Porter#start
 * @fires Porter#end
 * @fires Porter#deliver
 * @fires Porter#reject
 * @fires Porter#enter-destination
 * @fires Porter#over-destination
 * @fires Porter#exit-destination
 *
 * @param {DOMNode[]} dndContainerNodes - array of DOMNodes where porter will
 * look for packages and destinations
 *
 * @param [{Function}] options.accept - callback on drop end for custom accept logic.
 * function must return one of MOVE, COPY, or REJECT
 *
 * @param [{Boolean=true}] options.appendOnAcceptance - true if porter should
 * automatically append the package node to the destination node on acceptance
 *
 * @return {Porter}
 * @constructor
 *
 */
const Porter = function (dndContainerNodes, options = {}) {
	this.refresh(dndContainerNodes, options)
	return this
}

util.inherits(Porter, EventEmitter)

Porter.Delivery = Dnd.Delivery

/**
 * Re-runs the initial setup to account for any DOM structure changes, like
 * adding new package nodes
 */
Porter.prototype.refresh = function (dndContainerNodes = this.dndContainerNodes, options = this.options) {
	if (dndContainerNodes !== this.dndContainerNodes) {
		this.dndContainerNodes = dndContainerNodes
	}

	if (options !== this.options) {
		//keep the default behavior to append the node on acceptance and refresh
		//porter on drag end for backwards compatibility
		this.options = _.defaults(options, {appendOnAcceptance: true, refreshOnEnd: true})

		if (!options.accept) {
			options.accept = () => Porter.Delivery.MOVE
		}
	}

	this.dnd = new Dnd(this, this.options)
}

/**
 * Returns the state of all destinations. The key is the destination ID, and
 * the value is an array of objects with the following properties:
 *   - id: the ID of the package
 *   - node: the package's DOM node
 *
 * Example:
 * {
 * 		destination1: [
 * 			{ id: 'package1', node: [package1's DOM node] },
 * 			{ id: 'package2', node: [package2's DOM node] }
 * 		],
 * 		destination2: [
 * 			{ id: 'package3', node: [package3's DOM node] }
 * 		]
 * }
 */
Porter.prototype.val = function () {
	return _(this.dndContainerNodes)
		.map(destination => {
			let packageIds = packages(destination)
				.map(p => p.dataset.id)

			return [
				destination.dataset.id,
				packages(destination).map(p => {
					return {
						id: p.dataset.id,
						node: p
					}
				})
			]
		})
		.fromPairs().value()
}

/**
 * Moves the package matching the given DOM node to the given destination.
 *
 * @param packageNode DOM node for the package to move
 * @param newDestinationId ID of the new destination
 */
Porter.prototype.move = function (packageNode, newDestinationId) {
	packageNode.parentElement.removeChild(packageNode)

	let destination = findDestinationNode(this, newDestinationId)
	if (!_.find(packages(destination), p => p.dataset.id == packageNode.dataset.id)) {
		destination.appendChild(packageNode)
	}

	this.refresh()
}

/**
 * Deletes the package matching the given DOM node
 */
Porter.prototype.delete = function (packageNode) {
	packageNode.parentElement.removeChild(packageNode)

	this.refresh()
}

/**
 * Adds the given DOM node as a new package in the given destination
 */
Porter.prototype.add = function (destinationId, packageNode) {
	findDestinationNode(this, destinationId).appendChild(packageNode)

	this.refresh()
}

/**
 * Returns all package DOM nodes in the given destination
 */
Porter.prototype.packages = function (destinationId) {
	let destination = findDestinationNode(this, destinationId)
	return destination ? packages(destination) : []
}

/**
 * Destroys the porter's DOM nodes and removes its listeners
 */
Porter.prototype.remove = function () {
	_.invokeMap(this.dndContainerNodes, 'remove')
	this.removeAllListeners()
}

function findDestinationNode (porter, id) {
	return _.find(porter.dndContainerNodes, d => d.dataset.id == id)
}

function packages (destination) {
	return Array.from(destination.getElementsByClassName('porter-package'))
}

export default Porter
